import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Clock, Tag } from "lucide-react";
import Seo from "@/components/site/Seo";
import { trackEvent } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResourceDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      axios.get(`${API}/insights/${slug}`),
      axios.get(`${API}/insights`),
    ])
      .then(([detail, list]) => {
        setItem(detail.data);
        setRelated(list.data.filter((x) => x.slug !== slug).slice(0, 3));
        trackEvent("resource_read", { slug });
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-32 text-[#6B7280]">Loading…</div>
    );
  }

  if (error || !item) {
    return (
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-32">
        <h1 className="font-display text-4xl font-bold text-[#07111F]">Resource not found.</h1>
        <p className="mt-4 text-[#6B7280]">It may have been moved or renamed.</p>
        <Link to="/resources" className="mt-8 inline-flex items-center gap-2 text-[#6D4DFF] hover:gap-3 transition-all">
          <ArrowLeft className="w-4 h-4" /> Back to resources
        </Link>
      </section>
    );
  }

  return (
    <>
      <Seo
        title={`${item.title} | Xadentra Insights`}
        description={item.excerpt}
        path={`/resources/${item.slug}`}
      />

      {/* HEADER */}
      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 480, height: 480, background: "#6D4DFF", top: -180, left: -100, opacity: 0.5 }} />
        <div className="xa-blob" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -120, right: -120, opacity: 0.3 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-50 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 lg:py-24 relative">
          <Link
            to="/resources"
            data-testid="resource-back"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" /> All resources
          </Link>
          <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">
            {item.category}
          </div>
          <h1 className="font-display mt-5 text-4xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.02]">
            {item.title}
          </h1>
          <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-3xl">
            {item.excerpt}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/55 font-mono uppercase tracking-widest">
            <span className="inline-flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> {item.read_minutes} min read</span>
            <span className="inline-flex items-center gap-2"><Tag className="w-3.5 h-3.5" /> {item.category}</span>
            <span>{new Date(item.date).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
            {item.author && <span>By {item.author}</span>}
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-20">
          <article data-testid="resource-article" className="space-y-7">
            {(item.body || []).map((block, i) => {
              if (block.type === "h2") {
                return (
                  <h2 key={i} className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-[#07111F] pt-4">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "h3") {
                return (
                  <h3 key={i} className="font-display text-xl lg:text-2xl font-semibold tracking-tight text-[#07111F] pt-2">
                    {block.text}
                  </h3>
                );
              }
              if (block.type === "p") {
                return (
                  <p key={i} className="text-[#374151] leading-[1.8] text-base lg:text-lg">
                    {block.text}
                  </p>
                );
              }
              if (block.type === "ul") {
                return (
                  <ul key={i} className="space-y-3 pl-1">
                    {(block.items || []).map((it, j) => (
                      <li key={j} className="flex items-start gap-3 text-[#374151] leading-[1.75] text-base lg:text-lg">
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#6D4DFF] shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (block.type === "blockquote") {
                return (
                  <blockquote
                    key={i}
                    className="border-l-4 border-[#6D4DFF] pl-6 py-2 my-2 font-display text-lg lg:text-xl font-medium text-[#07111F] leading-snug italic"
                  >
                    “{block.text}”
                  </blockquote>
                );
              }
              if (block.type === "image" && block.src) {
                return (
                  <figure key={i} className="my-4">
                    <img
                      src={block.src}
                      alt={block.alt || ""}
                      loading="lazy"
                      className="w-full h-auto rounded-2xl border border-[#E5E7EB]"
                    />
                    {block.caption && (
                      <figcaption className="mt-2 text-xs text-[#6B7280] text-center">{block.caption}</figcaption>
                    )}
                  </figure>
                );
              }
              return null;
            })}
          </article>

          <div className="mt-16 pt-10 border-t border-[#E5E7EB]">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
              Next step
            </div>
            <h3 className="font-display mt-4 text-2xl lg:text-3xl font-bold tracking-tight text-[#07111F]">
              Want to apply this to your stack?
            </h3>
            <p className="mt-4 text-[#6B7280] leading-relaxed">
              We help B2B marketing teams turn principles like the above into shipped systems —
              Adobe Marketo Engage programs, Salesforce flows, dashboards your CMO actually trusts.
            </p>
            <Link
              to="/contact"
              data-testid="resource-cta"
              className="mt-7 inline-flex items-center gap-2 xa-btn xa-btn-primary"
            >
              Book a Working Session <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-[#F7F9FC] border-b border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">More insights</div>
            <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F]">
              Keep reading.
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((p, i) => (
                <Link
                  to={`/resources/${p.slug}`}
                  key={p.slug}
                  data-testid={`resource-related-${i}`}
                  className="xa-lift bg-white border border-[#E5E7EB] rounded-2xl p-7 hover:border-[#6D4DFF]/40 group flex flex-col"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">{p.category}</div>
                  <h3 className="font-display mt-4 text-lg font-semibold leading-snug text-[#07111F] group-hover:text-[#6D4DFF] transition-colors flex-1">
                    {p.title}
                  </h3>
                  <div className="mt-6 text-xs text-[#6B7280] inline-flex items-center gap-2">
                    {p.read_minutes} min read <ArrowUpRight className="w-3.5 h-3.5 text-[#6D4DFF]" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
